(ns minorodata.components.webforms.drawers
  (:require ["lucide-react" :refer [Plus X]]
            ["react" :as react]
            [cljs.spec.alpha :as spec]
            [clojure.string]
            [minorodata.app.handlers :refer [dispatch]]
            [minorodata.app.utils :as app.utils]
            [mold-forms.core :as form-component]
            [mold-forms.model :refer [->form-model]]
            [re-frame.core :as re-frame]
            [re-posh.core :as re-posh]
            [reagent.core :as reagent]
            [spec-tools.core :as st]
            [ui.button :refer [button]]
            [ui.dialog :refer [dialog-actions dialog-content dialog-root
                               dialog-title]]
            [ui.fields :as fields]
            [ui.table :as table]
            [ui.tabs :as tabs]
            [ui.typography :as typography]))


(defn use-mounted? []
  (let [mounted-ref (react/useRef false)]
    (react/useEffect
     (fn []
       (set! (.-current mounted-ref) true)
       #(set! (.-current mounted-ref) false))
     #js [])
    #(.-current mounted-ref)))


(defn open-webform [table-name]
  (dispatch [:app.events/navigate :webforms-table {:table-name table-name}]))


(defn submit-create-new-table [{:keys [on-success on-close]} form-data]
  (let [data (form-component/model->fields form-data)
        {:keys [table-name columns]} data
        clean-columns (map #(dissoc % :temp-id) columns)]
    (dispatch [:webforms.api/create-table {:data       {:table-name table-name
                                                        :columns    clean-columns}
                                           :on-success on-success
                                           :on-close   on-close}])))


(defn webform-from-existing-table [{:keys [to-new-table-tab]}]
  (let [{:webform-list/keys [tables]} @(re-posh/subscribe [:webform-list/pull])
        mounted? (use-mounted?)
        on-success #(when (mounted?) (open-webform %))
        submit (fn [form-data]
                 (let [data (form-component/model->fields form-data)]
                   (when (some? (:tablename data))
                     (dispatch [:webforms.api/create-form (assoc data :on-success on-success)]))))]
    [:div {:class "mt-4"}
     [typography/h2 "Create webform from existing table"]

     (if (empty? tables)
       [:div {:class "my-2"}
        [typography/p {:class "mt-1"} "You do not have any tables that can be managed using web forms."]
        [typography/p {:class "mt-1"} "Try creating a new one."]
        [dialog-actions
         [button {:on-click to-new-table-tab}
          "Create new table"]]]

       [:section
        [:div {:class "my-4"}
         [typography/p {:class "mt-1"}
          "You seem to have tables that can be added to webforms. Select which one you’d like to connect with:"]]

        [form-component/form
         (->form-model
          {:id        :new-webform
           :on-submit submit
           :clean-up  #(dispatch [:form-model/delete [:form-model/id :new-webform]])
           :fields    [{:name :tablename :value "" :process identity}]})
         (fn [fields errors on-submit]
           [:form {:on-submit on-submit :key fields}
            [:div
             [fields/select
              {:field    (:tablename fields)
               :errors   errors
               :id       :tablename
               :required true
               :label    "Table name"
               :native   false
               :options  (map (fn [{:webform-table/keys [tablename]}] {:value tablename})
                              tables)}]]

            [dialog-actions
             (let [loading? @(re-frame/subscribe [:mold-http/loading? :webforms.api/create-form])]
               [button {:type     "submit"
                        :disabled loading?
                        :loading  loading?}
                "Create webform"])]])]])]))


;; TODO: decide if this is needed in the mold forms.
;;
;; The idea is that this makes a field, that is compatible with default field
;; API (has :action and :value), but rather than working with top level fields,
;; it can access maps nested inside vectors.
;;
;; This is useful when you need to have a list of editable entities.
;;
(defn make-subfield [{:keys [field idx subfield process]}]
  (let [{:keys [action value]} field]
    {:action (fn [evt]
               (let [subfield-value ((or process identity) evt)]
                 (action (assoc-in value [idx subfield] subfield-value))))
     :value (get-in value [idx subfield])}))


(defn remove-column-at-index [field idx]
  (let [{:keys [action value]} field]
    (action (app.utils/remove-at-index value idx))))


(def allowed-column-types
  ;; All column types supported by the back end
  ;; ["text" "number" "float" "date" "date-time"]

  ;; Column types enabled for the 1st interation of webform
  ;; table creation thing
  ["text" "number"])


(defn column-form [{:keys [field idx errors]}]
  [table/tr
   [table/td
    [fields/input
     {:field       (make-subfield {:field field :idx idx :subfield :name :process form-component/event->value})
      :errors      errors
      :placeholder "column_name"
      :data-cy     "webforms-drawer-new-table-column-name-input"
      :id          :column-name

      ;; do not enable autofocus for the first entry, as it will steal focus
      ;; from table name input
      :auto-focus (< 0 idx)
      :required   true

      :class "w-48 min-w-0"}]]

   [table/td {:width "30%"}
    [fields/select
     {:field    (make-subfield {:field field :idx idx :subfield :type})
      :errors   errors
      :id       :column-type
      :required true
      :native   false
      :variant  "standard"
      :options  (map (fn [t] {:value t}) allowed-column-types)}]]

   [table/td {:width "20%"}
    [fields/switch
     {:field    (make-subfield {:field field :idx idx :subfield :nullable?})
      :errors   errors
      :id       :column-nullable?
      :variant  "standard"}]]

   [table/td {:width "50px"}
    [button {:icon     X
             :size     :icon
             :variant  :empty-transparent
             :title    "Remove column"
             :on-click #(remove-column-at-index field idx)}]]])


(defn default-column []
  {:name      ""
   :type      "text"
   :nullable? false
   :temp-id   (random-uuid)})


(defn append-new-column [field]
  (let [{:keys [value action]} field]
    (action (conj value (default-column)))))


(spec/def ::table-name
  (spec/and (st/spec string? {:reason "Table name should be a string"})
            (st/spec (complement clojure.string/blank?) {:reason "Table name can not be empty"})
            (st/spec #(re-matches #"(?i)^[a-z\d_]+$" %) {:reason "Table name should only include letters, numbers and underscores"})))


(spec/def ::name
  (spec/and (st/spec string? {:reason "Column name should be a string"})
            (st/spec (complement clojure.string/blank?) {:reason "Column name can not be empty"})
            (st/spec #(re-matches #"(?i)^[a-z\d_]+$" %) {:reason "Column name should only include letters, numbers and underscores"})))


(spec/def ::type #{"text" "number" "float" "date" "date-time"})


(spec/def ::nullable? boolean?)


(spec/def ::temp-id uuid?)


(spec/def ::webform-column
  (spec/keys :req-un [::name ::type ::nullable? ::temp-id]))


(spec/def ::columns
  (spec/coll-of ::webform-column :kind vector))


(spec/def ::new-webform-table-form
  (spec/keys :req-un [::table-name ::columns]))


;; TODO: push updates to mold-forms
;; Note that error id will now be a vector, so everything in
;; minorodata.components.forms.inputs will have to be updated as well
;;
;; The idea is that we currently have validator return wrong keys and even 
;; those wrong keys can not point to nested values.
;;
;; It worked well on flat forms, but breaks down on nested ones.

(defn validate-form
  "Validate form data with spec"
  [spec form-state]
  (when-let [problems (st/explain-data spec form-state)]
    (->> (::spec/problems problems)
         (reduce (fn [errs {:keys [in reason]}]
                   (update errs in conj reason))
                 {}))))


(defn create-table [{:keys [on-close]}]
  (let [mounted?        (use-mounted?)
        callbacks       {:on-success #(when (mounted?) (open-webform %))
                         :on-close   #(when (mounted?) (on-close))}
        initial-columns [(default-column)]]
    [:div {:class "mt-4"}
     [typography/h2
      "Create new table to work with webforms"]

     [typography/p {:class "mt-1"}
      [:span
       "We will create a new table for you. Our system will append "
       [:b [:code "_wf"]]
       " to the name you provide so we can discern webform table from regular tables."]]

     [form-component/form
      (->form-model
       {:id        :new-webform-table
        :on-submit (partial submit-create-new-table callbacks)
        :cleanup   #(dispatch [:form-model/delete [:form-model/id :new-webform-table]])
        :validate  (partial form-component/validate-form ::new-webform-table-form)
        :fields    [{:name :table-name :value ""}
                    {:name :columns :value initial-columns :process identity}]})
      (fn [fields errors on-submit]
        (let [table-name (get-in fields [:table-name :value])
              loading?   @(re-frame/subscribe [:mold-http/loading? :webforms.api/create-table])
              error      @(re-posh/subscribe [:webform-list/prop :webform-list/new-table-error])]
          [:form {:class "my-4" :on-submit on-submit :key fields}

           [:div
            [fields/input
             {:field      (:table-name fields)
              :data-cy    "webforms-drawer-new-table-name-input"
              :errors     errors
              :id         :table-name
              :auto-focus true
              :required   true
              :label      "Table name"}]]

           (when-not (clojure.string/blank? table-name)
             [typography/p
              "New table will be named: "
              [:b [:code table-name "_wf"]]])

           [:div {:class "my-5"}
            [typography/h3 "Table columns"]]

           [:div
            [table/table
             [table/thead
              [table/tr
               [table/th "Column name"]
               [table/th "Type"]
               [table/th "Nullable"]
               [table/th]]]
             [table/tbody
              (map-indexed (fn [idx column]
                             ^{:key (:temp-id column)}
                             [column-form {:field  (:columns fields)
                                           :errors errors
                                           :idx    idx
                                           :column column}])
                           (get-in fields [:columns :value]))]]]

           [:div {:class "mt-4"}
            [button
             {:variant  :secondary
              :icon     Plus
              :on-click #(append-new-column (:columns fields))}
             "Add column"]]

           ;; TODO: A hack needed to overcome an issue with not all errors
           ;; being cleared.
           ;; Will nede to remove this once we have a proper support for nested
           ;; forms in mold-forms
           (when-let [column-name-error (:columns errors)]
             (when-not (= (get-in fields [:columns :value])
                          initial-columns)
               [:div {:class "mt-4"}
                [typography/p {:class "text-kleene-text-error"}
                 (:value column-name-error)]]))

           (when (some? error)
             [:div {:class "mt-4"}
              [typography/p {:class "text-kleene-text-error"} error]])


           [dialog-actions
            [button {:type     "submit"
                     :data-cy  "webforms-drawer-create-new-table-button"
                     :disabled loading?
                     :loading  loading?}
             "Create new table"]]]))]]))

(def drawer-tabs
  [{:name "Existing table" :key "from-existing-table" :data-cy "webforms-drawer-tab-existing-table"}
   {:name "New table" :key "create-table" :data-cy "webforms-drawer-tab-new-table"}])

(defn create-webform-panel [_]
  (let [tab (reagent/atom "from-existing-table")]
    (dispatch [:webform-list/update {:webform-list/new-table-error ""}])
    (fn [{:keys [on-close]}]
      (let [selected-tab @tab]
        [tabs/tabs-root {:value           selected-tab
                         :class           "flex flex-col h-full overflow-hidden"
                         :on-value-change (fn [id]
                                            (dispatch [:webform-list/update {:webform-list/new-table-error ""}])
                                            (reset! tab id))}
         [:header {:class "shrink-0 flex items-center justify-between gap-4 border-b px-6 pt-4"}
          [tabs/tabs-list {:variant :underlined
                           :class   "border-b-0"}
           (for [{:keys [key name data-cy]} drawer-tabs]
             ^{:key key}
             [tabs/tabs-trigger {:value   key
                                 :variant :underlined
                                 :size    :small
                                 :class   "flex-auto"
                                 :data-cy data-cy}
              name])]
          [:button {:type       "button"
                    :aria-label "Close"
                    :class      "shrink-0 cursor-pointer"
                    :data-cy    "webform-create-close-btn"
                    :on-click   on-close}
           [:> X {:size 20}]]]

         [:div {:class "flex-1 overflow-y-auto px-6 pb-kai"}
          (for [t (mapv :key drawer-tabs)]
            ^{:key t}
            [tabs/tabs-content {:value t :class "h-full"}
             (case selected-tab
               "from-existing-table" [:f> webform-from-existing-table {:to-new-table-tab #(reset! tab "create-table")}]
               "create-table"        [:f> create-table {:on-close on-close}]

               [:div])])]]))))


(defn delete-webform [{:keys [open on-close on-success webform]}]
  [dialog-root {:open     open
                :on-open-change #(when (not %) (on-close))}
   [dialog-content
    [dialog-title {:class "min-w-0"}
     "Do you really wish to delete '" (:webform/tablename webform) "'?"]

    [dialog-actions
     [button {:data-cy  :webform-delete-confirm-btn
              :on-click (fn []
                          (on-close)
                          (dispatch [:webforms.api/delete-form {:webform-id (:webform/id webform)
                                                                :on-success on-success}]))}
      "Delete webform"]]]])
